import styled from 'styled-components';

import { Button } from '#/components/Button/Button';
import { Status } from '#/components/Status/Status';
import { useActions } from '#/hooks/actions/useActions';
import { useConfig } from '#/hooks/useConfig';
import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { AppStatus, ButtonVariant } from '#/service/constants';
import { remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

interface Props {
  /** Title of the Status component. Already translated. */
  title: string;
  /** Message of the Status component. Already translated. */
  message: string;
  /**
   * Click handler for the default close button (a configured button without an action defined),
   * or the fallback close button (if no buttons are configured at all).
   */
  onDefaultClose: () => void;
}

const Message = styled.p`
  margin: 0;
`;

const ButtonContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  margin-top: ${remify(24)};
  margin-inline: auto;
  gap: ${remify(16)};

  @media screen and ${MEDIA_QUERIES.lg} {
    gap: ${remify(8)};
  }
`;

/** Subscription purchase success screen, with multiple configurable buttons, and background image. */
export const PurchaseSuccess = ({ title, message, onDefaultClose }: Props) => {
  const { handleAction } = useActions();
  const { featureConfig } = useConfig();
  const { successfulPurchaseBackground } = featureConfig.purchase?.images ?? {};
  const { buttons } = featureConfig.purchase?.inApp?.successScreen ?? {};

  return (
    <Status
      applyDefaultGradient={false}
      backgroundImageUrl={successfulPurchaseBackground}
      status={AppStatus.Success}
      statusTitle={title}
    >
      <Message>{message}</Message>
      <ButtonContainer>
        {buttons?.length ? (
          // Since the inApp feature is enabled Core SHOULD always pass at least one button. It's not a guarantee though, hence the optional type.
          buttons.map(({ action, display }, index) => (
            <Button
              isFullWidth={true}
              key={display.title}
              onClick={() => (action ? handleAction(action) : onDefaultClose())}
              variant={
                // Requirement: the first button should always be the primary.
                index ? ButtonVariant.Secondary : ButtonVariant.Primary
              }
            >
              {tb(display.title)}
            </Button>
          ))
        ) : (
          // Otherwise, just render a fallback button.
          <Button onClick={onDefaultClose} variant={ButtonVariant.Primary}>
            {tb('iap.success.button')}
          </Button>
        )}
      </ButtonContainer>
    </Status>
  );
};
